import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';
import TrackerDetailModal from '../components/TrackerDetailModal';
import TrackerGroupsPanel, { GroupsBadge } from '../components/TrackerGroupsPanel';
import { useAutoRefresh, AutoRefreshBadge } from '../hooks/useAutoRefresh';
import { getRecoveryStatus, RECOVERY_TABS } from '../utils/recoveryStatus';
import { getTrackerRows } from '../utils/trackerUtils';

export default function Recovery() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('recovering');
  const [recoveries, setRecoveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [chainFilter, setChainFilter] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [chartModal, setChartModal] = useState({ isOpen: false, chain: '', tokenAddress: '', tokenSymbol: '' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [trackerModal, setTrackerModal] = useState({
    isOpen: false,
    tracker: null,
    tokenSymbol: '',
    currentMarketCap: null,
    recoverySnapshot: null,
  });

  const toggleExpanded = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getTrackingGroups = (recovery) => getTrackerRows(recovery);

  const getGroupCount = (recovery) => {
    if (recovery.trackerCount != null) return recovery.trackerCount;
    return getTrackingGroups(recovery).length;
  };

  const getRecoverySnapshot = (recovery) => ({
    bottomMarketCap: recovery.bottomMarketCap,
    bottomReachedAt: recovery.bottomReachedAt,
    recoveryPercentage: recovery.recoveryPercentage,
    recoveryFromAthPercentage: recovery.recoveryFromAthPercentage,
    allTimeHigh: recovery.allTimeHigh,
    drawdownPercentage: recovery.drawdownPercentage,
  });

  const openTrackerModal = ({ tracker, tokenSymbol, currentMarketCap, recoverySnapshot }) => {
    setTrackerModal({
      isOpen: true,
      tracker,
      tokenSymbol,
      currentMarketCap,
      recoverySnapshot,
    });
  };

  const allMilestonesForRecovery = (recovery) => {
    const groups = getTrackingGroups(recovery);
    const set = new Set();
    for (const g of groups) {
      for (const m of g.recoveryMilestonesReached || []) set.add(m);
    }
    if (set.size === 0 && recovery.recoveryMilestonesReached?.length) {
      recovery.recoveryMilestonesReached.forEach((m) => set.add(m));
    }
    return [...set].sort((a, b) => a - b);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      setTimeout(() => setCopiedAddress(null), 2000);
    });
  };

  const fetchRecoveries = useCallback(async () => {
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('status', activeTab);
      if (chainFilter) params.append('chain', chainFilter);
      params.append('limit', '100');
      params.append('includeMcSkip', '0');

      const response = await api.get(`/analytics/recovery?${params.toString()}`);
      setRecoveries(response.data.recoveries);
      setStats(response.data.stats);

      // Enrich Master Chef column in background (batched on server — does not block first paint).
      api.get(`/analytics/recovery?${params.toString().replace('includeMcSkip=0', 'includeMcSkip=1')}`)
        .then((mcRes) => {
          setRecoveries(mcRes.data.recoveries);
        })
        .catch(() => { /* MC column stays empty */ });
    } catch (err) {
      console.error('Error fetching recovery data:', err);
      setError('Failed to load recovery data');
    }
  }, [activeTab, chainFilter]);

  const { loading, refreshing, lastUpdated, refresh } = useAutoRefresh(fetchRecoveries, [fetchRecoveries]);

  const formatMarketCap = (mc) => {
    if (mc >= 1000000000) return `$${(mc / 1000000000).toFixed(2)}B`;
    if (mc >= 1000000) return `$${(mc / 1000000).toFixed(2)}M`;
    if (mc >= 1000) return `$${(mc / 1000).toFixed(2)}K`;
    return `$${mc.toFixed(0)}`;
  };

  const getChainEmoji = (chain) => {
    const emojis = {
      solana: '◎',
      base: '🔵',
      ethereum: '⟠',
      bsc: '🟡'
    };
    return emojis[chain] || '⛓️';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const generateTradeUrl = (chain, address) => {
    // Axiom supports Solana and BSC
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    // GMGN.ai supports Base and Ethereum
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    return null;
  };

  const getTradeButtonLabel = (chain) => {
    if (chain === 'solana' || chain === 'bsc') {
      return 'Axiom';
    }
    if (chain === 'base' || chain === 'ethereum') {
      return 'GMGN';
    }
    return 'Trade';
  };

  const getFomoUrl = (chain, address) => {
    const slug = { solana: 'solana', ethereum: 'ethereum', base: 'base', bsc: 'bnb' }[chain];
    return slug && address ? `https://fomo.family/tokens/${slug}/${address}` : null;
  };

  // Chart URL generators
  const getAxiomChartUrl = (chain, address) => {
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    return null;
  };

  const getDexScreenerUrl = (chain, address) => {
    const chainMap = {
      solana: 'solana',
      base: 'base',
      ethereum: 'ethereum',
      bsc: 'bsc'
    };
    return `https://dexscreener.com/${chainMap[chain] || chain}/${address}`;
  };

  const getGmgnUrl = (chain, address) => {
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    if (chain === 'solana') {
      return `https://gmgn.ai/sol/token/${address}`;
    }
    return null;
  };

  const getRecoveryColor = (percent) => {
    if (percent >= 100) return 'text-green-600';
    if (percent >= 50) return 'text-blue-600';
    if (percent >= 20) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getDrawdownColor = (percent) => {
    if (percent <= -80) return 'text-red-700';
    if (percent <= -70) return 'text-red-500';
    return 'text-orange-500';
  };

  const mcSkipBadge = (recovery) => {
    const status = recovery.mcSkipStatus;
    const reason = recovery.mcSkipReason;
    if (!reason) {
      return <span className="text-xs text-gray-400">—</span>;
    }
    const styles = {
      ready: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      skip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      position: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      disabled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };
    const labels = {
      ready: 'Would buy',
      skip: 'Skipped',
      position: 'In trade',
      disabled: 'MC off',
    };
    return (
      <div className="max-w-[220px]">
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${styles[status] || styles.skip}`}>
          {labels[status] || 'Skipped'}
        </span>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-snug" title={reason}>
          {reason}
        </p>
      </div>
    );
  };

  if (loading && recoveries.length === 0 && !stats) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Token Recovery Tracker</h1>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading recovery data...</div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${refreshing ? 'opacity-90' : ''} transition-opacity`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">📈 Token Recovery Tracker</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            One row per token · expand multi-group rows to see per-chat recovery state
          </p>
          <AutoRefreshBadge refreshing={refreshing} lastUpdated={lastUpdated} className="mt-1" />
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Tracking</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">Recovery</div>
            <div className="text-3xl font-bold text-green-600">{stats.recovering ?? 0}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">Drawdown</div>
            <div className="text-3xl font-bold text-red-600">{stats.drawdown ?? 0}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">Recovered</div>
            <div className="text-3xl font-bold text-blue-600">{stats.recovered ?? 0}</div>
          </div>
        </div>
      )}

      {/* Status sub-tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4">
          <nav className="flex gap-1 -mb-px overflow-x-auto" aria-label="Recovery status">
            {RECOVERY_TABS.map((tab) => {
              const count = stats?.[tab.id] ?? 0;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
        <p className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
          {RECOVERY_TABS.find((t) => t.id === activeTab)?.description}
          {' '}Expand groups to see per-chat IMC, milestones, and recovery alerts — click a tracker for details.
        </p>
        <div className="p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Chain
            </label>
            <select
              value={chainFilter}
              onChange={(e) => setChainFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900"
            >
              <option value="">All chains</option>
              <option value="solana">Solana</option>
              <option value="base">Base</option>
              <option value="ethereum">Ethereum</option>
              <option value="bsc">BSC</option>
            </select>
          </div>
          {chainFilter && (
            <button
              type="button"
              onClick={() => setChainFilter('')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300"
            >
              Clear chain filter
            </button>
          )}
        </div>
      </div>

      {/* Recovery List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Token
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Bottom → Current
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Recovery %
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                From ATH
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Groups
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Milestones
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Master Chef
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Last Updated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {recoveries.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  No tokens in{' '}
                  <span className="font-medium">{RECOVERY_TABS.find((t) => t.id === activeTab)?.name}</span>.
                  {activeTab === 'drawdown' && ' Tokens appear after a deep drop from ATH.'}
                  {activeTab === 'recovering' && ' Tokens move here after a 10%+ bounce from bottom.'}
                  {activeTab === 'recovered' && ' Tokens move here at 10x+ from tracked bottom or when price reaches ATH.'}
                </td>
              </tr>
            ) : (
              recoveries.flatMap((recovery) => {
                const groups = getTrackingGroups(recovery);
                const groupCount = getGroupCount(recovery);
                const isExpanded = expandedRows.has(recovery._id);
                const milestones = allMilestonesForRecovery(recovery);

                const mainRow = (
                <tr key={recovery._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="text-lg mr-2">{getChainEmoji(recovery.chain)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/token/${recovery.chain}/${recovery.tokenAddress}`)}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400"
                          >
                            {recovery.tokenSymbol}
                          </button>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {recovery.tokenName}
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                          {recovery.tokenAddress.substring(0, 12)}...{recovery.tokenAddress.slice(-8)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const status = recovery.displayStatus || getRecoveryStatus(recovery);
                      if (status === 'recovered') {
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:text-blue-300">
                            ✅ Recovered
                          </span>
                        );
                      }
                      if (status === 'recovering') {
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:text-green-300">
                            📈 Recovery
                          </span>
                        );
                      }
                      return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          📉 Drawdown
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="text-gray-500 text-xs">Bottom</div>
                    <div className="font-medium text-red-600">
                      {formatMarketCap(recovery.bottomMarketCap)}
                    </div>
                    <div className="text-gray-500 text-xs mt-1">Current</div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatMarketCap(recovery.currentMarketCap)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-lg font-bold ${getRecoveryColor(recovery.recoveryPercentage)}`}>
                      +{recovery.recoveryPercentage.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">from bottom</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-lg font-bold ${getDrawdownColor(recovery.recoveryFromAthPercentage)}`}>
                      {recovery.recoveryFromAthPercentage.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ATH: {formatMarketCap(recovery.allTimeHigh)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <GroupsBadge
                      item={recovery}
                      expandedRows={expandedRows}
                      toggleExpanded={toggleExpanded}
                      rowKey={recovery._id}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {milestones.length > 0 ? (
                        milestones.map((milestone) => (
                          <span
                            key={milestone}
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                          >
                            +{milestone}%
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">None yet</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {mcSkipBadge(recovery)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(recovery.lastUpdatedAt)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {getFomoUrl(recovery.chain, recovery.tokenAddress) && (
                        <a
                          href={getFomoUrl(recovery.chain, recovery.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="View on Fomo"
                        >
                          FOMO
                        </a>
                      )}
                      {getAxiomChartUrl(recovery.chain, recovery.tokenAddress) && (
                        <a
                          href={getAxiomChartUrl(recovery.chain, recovery.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                          title="View on Axiom"
                        >
                          AXI
                        </a>
                      )}
                      <a
                        href={getDexScreenerUrl(recovery.chain, recovery.tokenAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        title="View on DexScreener"
                      >
                        DEX
                      </a>
                      {getGmgnUrl(recovery.chain, recovery.tokenAddress) && (
                        <a
                          href={getGmgnUrl(recovery.chain, recovery.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          title="View on GMGN"
                        >
                          GMGN
                        </a>
                      )}
                      <button
                        onClick={() => setChartModal({
                          isOpen: true,
                          chain: recovery.chain,
                          tokenAddress: recovery.tokenAddress,
                          tokenSymbol: recovery.tokenSymbol
                        })}
                        className="text-xs px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                        title="Open Chart Preview"
                      >
                        Chart
                      </button>
                      <button
                        onClick={() => copyToClipboard(recovery.tokenAddress)}
                        className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                        title="Copy contract address"
                      >
                        {copiedAddress === recovery.tokenAddress ? '✓' : '📋'}
                      </button>
                    </div>
                  </td>
                </tr>
                );

                const expandRow = isExpanded ? (
                  <TrackerGroupsPanel
                    key={`${recovery._id}-groups`}
                    item={recovery}
                    tokenSymbol={recovery.tokenSymbol}
                    currentMarketCap={recovery.currentMarketCap}
                    recoverySnapshot={getRecoverySnapshot(recovery)}
                    onTrackerClick={openTrackerModal}
                    colSpan={10}
                  />
                ) : null;

                return expandRow ? [mainRow, expandRow] : [mainRow];
              })
            )}
          </tbody>
        </table>
      </div>

      {/* DexScreener Chart Modal */}
      <DexScreenerModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ ...chartModal, isOpen: false })}
        chain={chartModal.chain}
        tokenAddress={chartModal.tokenAddress}
        tokenSymbol={chartModal.tokenSymbol}
      />

      <TrackerDetailModal
        isOpen={trackerModal.isOpen}
        onClose={() => setTrackerModal({ ...trackerModal, isOpen: false })}
        tracker={trackerModal.tracker}
        tokenSymbol={trackerModal.tokenSymbol}
        currentMarketCap={trackerModal.currentMarketCap}
        recoverySnapshot={trackerModal.recoverySnapshot}
      />
    </div>
  );
}
